import { Component, DestroyRef, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe, DatePipe, Location } from '@angular/common';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { FormlyModule } from '@ngx-formly/core';
import { format } from 'date-fns-tz';
import { add } from 'date-fns';
import { ApiKey } from '@parashift/shared/models';
import { ErrorResponse, ApiKeyService, NotificationService, Logger, CurrentTenantRegister } from '@parashift/shared/services';
import { ModalService } from '@parashift/shared/components';
import { ApiKeyModalComponent } from './api-key-modal/api-key-modal.component';
import { ApiKeysEditFormComponent } from './api-keys-edit-form/api-keys-edit-form.component';
import { HeaderActionStore } from '@parashift/shared/stores';

@Component({
  selector: 'api-keys-edit',
  templateUrl: './api-keys-edit.component.html',
  imports: [
    ApiKeysEditFormComponent,
    AsyncPipe,
    FormlyModule,
    ReactiveFormsModule
]
})
export class ApiKeysEditComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private apiKeyService = inject(ApiKeyService);
  private currentTenantRegister = inject(CurrentTenantRegister);
  private datePipe = inject(DatePipe);
  private modalService = inject(ModalService);
  private location = inject(Location);
  private notificationService = inject(NotificationService);
  private headerActionStore = inject(HeaderActionStore);
  private logger = inject(Logger);
  private destroyRef = inject<DestroyRef>(DestroyRef);

  @ViewChild(ApiKeysEditFormComponent, { static: false }) private formComponent: ApiKeysEditFormComponent;

  protected _model$ = new BehaviorSubject<ApiKey['plainModel']>(undefined);
  readonly model$ = this._model$.asObservable();
  get model() { return this._model$.getValue(); }

  id: string;
  api_key: ApiKey;
  error: ErrorResponse;
  disableForm = false;
  tenant_id: number;

  constructor() {
    const destroyRef = this.destroyRef;

    destroyRef.onDestroy(() => this.headerActionStore.destroy());
  }

  ngOnInit() {
    this.headerActionStore.addActions({ backButton: { dispatch: () => this.goToListView() }});

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params: ParamMap) => {
      this.tenant_id = Number(params.get('tenant_id'));
      this.id = params.get('id');

      if (this.id !== 'new') {
        this.getApiKey(params.get('id'));
      } else {
        this.initApiKey();
      }
    });
  }

  getApiKey(id: string) {
    this.apiKeyService.findRecord(id)
      .subscribe({
        next: (api_key: ApiKey) => {
          this.setApiKey(api_key);
        },
        error: (error: ErrorResponse) => {
          this.id = null;
          this.error = error;
          this.logger.log('api-keys-edit-component', 'API KEY Error', error);
        }
      });
  }

  setApiKey(api_key: ApiKey) {
    this.addDeleteButton();
    this.api_key = api_key;
    this.id = api_key.id;
    this._model$.next(this.api_key.plainModel);
  }

  initApiKey() {
    const now = new Date();
    const proposal = add(now, { months: 6 });

    this.api_key = new ApiKey({
      tenant_id: this.tenant_id,
      name: `${this.currentTenantRegister.tenant.name} ${this.datePipe.transform(now, 'dd.MM.yyyy')}`,
      expires_at: this.datePipe.transform(proposal, 'yyyy-MM-dd')
    });

    this._model$.next(this.api_key.plainModel);
    this.headerActionStore.addActions({ saveButton: { dispatch: () => this.api_key && this.formComponent.submit() }});
  }

  saveApiKey(model: ApiKey['plainModel']) {
    this.disableForm = true;
    this.api_key.plainModel = model;
    this.api_key.expires_at = format(model.expires_at, 'yyyy-MM-dd\'T\'HH:mm:ssxxx');

    this.apiKeyService.saveRecord(this.api_key).subscribe({
      next: (api_key: ApiKey) => {
        this.api_key = api_key;
        this._model$.next(this.api_key.plainModel);

        this.notificationService.success({
          description: $localize `:@@components.api_keys.api_key_save_success:The API key has been successfully saved.`,
          title: $localize `:@@components.api_keys.api_key_saved:API key saved`
        });

        if (this.id === 'new') {
          this.id = this.api_key.id = api_key.id;
          this.createAndReplaceUrl();
          this.headerActionStore.removeActions(['saveButton']);
          this.showApiKeyModal(api_key);
        }
        this.addDeleteButton();
      },
      error: (error: ErrorResponse) => {
        this.logger.log('Save ApiKey Error', error);
        this.notificationService.formattedErrorResponse(error);
        this.disableForm = false;
      }
    });
  }

  deleteApiKey() {
    if (!this.id || this.id === 'new') {
      return;
    }

    this.modalService
      .confirmation({
        initialState: {
          title: $localize `:@@components.api_keys.delete_api_key:Delete API key`,
          description: $localize `:@@components.api_keys.delete_api_key_confirm:Do you want to delete this API key?`
        }
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.apiKeyService.deleteRecord(this.id).subscribe({
          next: () => {
            this.notificationService.success({
              description: $localize `:@@components.api_keys.api_key_delete_success:The api key has been successfully deleted.`,
              title: $localize `:@@components.api_keys.api_key_deleted:Webhook deleted`
            });
            this.goToListView();
          },
          error: (error: ErrorResponse) => {
            this.notificationService.formattedErrorResponse(error);
          }
        });
      });
  }

  createAndReplaceUrl() {
    const url = this.router.createUrlTree(['../../', this.id, 'edit'], { relativeTo: this.route }).toString();
    this.location.replaceState(url);
    this.headerActionStore.replaceId(this.id);
  }

  goToListView() {
    this.router.navigate(['../../'], { relativeTo: this.route });
  }

  private showApiKeyModal(model: ApiKey) {
    const initialState = { model };

    this.modalService.customized({ initialState }, ApiKeyModalComponent)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.goToListView();
      });
  }

  private addDeleteButton() {
    this.headerActionStore.addMenuItems([{
      dispatch: () => this.api_key && this.deleteApiKey(),
      identifier: 'delete'
    }]);
  }
}
